import { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createUseStyles } from 'react-jss';
import { Trans, useTranslation } from 'react-i18next';

import { Theme } from '@app/theme';
import { useTrack } from '@app/analytics';

import {
  ApiException,
  DevicesCategory,
  Participation,
  SessionQuota,
  useBookMeeting,
  useClearAvailableDatesCache,
  useHaveQuota,
  usePaginatedAvailableDates,
  useParticipant,
  usePublicSession,
  useUpdateParticipantField,
} from '@app/api';
import {
  CenteredButton,
  isSessionOpen,
  PublicBackground,
  useBoolModalState,
  useToast,
} from '@app/core';
import {
  useLoginParticipantWithModal,
  VerifyPhoneViaCodeModal,
} from '@app/auth';
import { CheckboxBlockField } from '@app/form';

import {
  MeetingDateSelectionForm,
  MeetingDateSelectionFormValues,
} from '../components/MeetingDateSelectionForm';
import { ParticipationEditSummary } from '../components/ParticipationEditSummary';
import { StudyFormatAndRequirements } from '../components/StudyFormatAndRequirements';
import classNames from 'classnames';
import { CompletedSession } from '../../StudyApplication/CompletedSession';
import { MeetingDeviceSelection } from '../components/MeetingDeviceSelection';
import { parsePhoneNumber } from 'libphonenumber-js';

const useStyles = createUseStyles((theme: Theme) => ({
  hideableSection: {
    maxHeight: 0,
    overflow: 'hidden',
    transition: 'max-height 0.5s ease-in-out',
  },
  hideableSectionVisible: {
    maxHeight: 50000,
  },
  section: {
    marginTop: theme.marginBase * 3,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.marginBase * 3,
  },
}));

interface Props {
  participation: Participation;
}

export const BookForMeetingPage = ({ participation }: Props) => {
  const classes = useStyles();

  const navigate = useNavigate();
  const toast = useToast();
  const login = useLoginParticipantWithModal();
  const { t } = useTranslation();
  const track = useTrack();

  const hideableSectionRef = useRef<HTMLDivElement | null>(null);
  const { data: session, isLoading } = usePublicSession(
    participation.sessionLinkId || participation.sessionId,
  );
  const haveDeviceQuota = useHaveQuota(
    session?.id,
    SessionQuota.ALLOWED_DEVICES_CATEGORY,
  );
  const displayDeviceSelect =
    haveDeviceQuota &&
    session?.availableDevicesCategories &&
    session.availableDevicesCategories.length > 1;

  const [hasAcceptedRequirements, setHasAcceptedRequirements] = useState(false);
  const onToogleAgreementConsent = useCallback(() => {
    setHasAcceptedRequirements((hasAccepted) => !hasAccepted);
  }, [setHasAcceptedRequirements]);

  const [displayAppointmentSelection, setDisplayAppointmentSelection] =
    useState(false);

  const [selectedDevice, setSelectedDevice] = useState<DevicesCategory | null>(
    null,
  );

  const [
    phoneVerificationOpened,
    openPhoneVerification,
    closePhoneVerification,
  ] = useBoolModalState();
  const [pendingValues, setPendingValues] =
    useState<MeetingDateSelectionFormValues | null>(null);

  const { data: participant, refetch: refetchParticipant } = useParticipant(
    participation.participantId,
    {
      retry: false,
    },
  );

  const paginatedAvailableDates = usePaginatedAvailableDates(
    session?.taskSettingId,
  );
  const clearAvailableDatesCache = useClearAvailableDatesCache(
    session?.taskSettingId,
  );

  const { mutate: updateParticipant } = useUpdateParticipantField(
    participation.participantId,
  );

  const { mutateAsync } = useBookMeeting(participation.id);

  const onBookATimeClick = useCallback(() => {
    setDisplayAppointmentSelection((shouldDisplay) => !shouldDisplay);
    const sectionRef = hideableSectionRef.current;
    if (sectionRef) {
      setTimeout(() => {
        sectionRef.scrollIntoView({
          block: 'start',
          behavior: 'smooth',
        });
      }, 200);
    }
  }, []);

  const completeBooking = useCallback(
    async ({ date, timezone }: MeetingDateSelectionFormValues) => {
      try {
        if (!date || !participation.participantId) {
          return;
        }

        if (timezone) {
          updateParticipant({ fieldName: 'timezone', text: timezone });
        }

        const commitedDevice = haveDeviceQuota
          ? selectedDevice || session?.availableDevicesCategories?.[0] || null
          : null;
        if (!selectedDevice) {
          setSelectedDevice(commitedDevice);
        }

        track('Meeting Booked', {
          participantId: participation.participantId,
          sessionId: participation.sessionId,
        });

        await mutateAsync({
          date,
          selectedDevice: commitedDevice,
        });

        navigate(`../?success=true`);
      } catch (e) {
        if (e instanceof ApiException && e.response?.status === 409) {
          toast.error(t('booking.bookAccepted.conflict'));
          clearAvailableDatesCache();
        } else {
          toast.exception(e);
        }
      }
    },
    [
      participation.participantId,
      participation.sessionId,
      haveDeviceQuota,
      selectedDevice,
      session?.availableDevicesCategories,
      track,
      mutateAsync,
      navigate,
      updateParticipant,
      toast,
      t,
      clearAvailableDatesCache,
    ],
  );

  const onSelectAppointment = useCallback(
    async (values: MeetingDateSelectionFormValues) => {
      try {
        if (!values.date || !participation.participantId) {
          return;
        }

        await login(participation.participantId);
        const { data: updatedParticipant } = await refetchParticipant();

        if (!updatedParticipant?.phoneValidated) {
          setPendingValues(values);
          openPhoneVerification();
          return;
        }

        await completeBooking(values);
      } catch (e) {
        toast.exception(e);
      }
    },
    [
      participation.participantId,
      login,
      refetchParticipant,
      openPhoneVerification,
      completeBooking,
      toast,
    ],
  );

  const onPhoneVerified = useCallback(async () => {
    closePhoneVerification();
    if (pendingValues) {
      await completeBooking(pendingValues);
      setPendingValues(null);
    }
  }, [closePhoneVerification, completeBooking, pendingValues]);

  if (session && !isSessionOpen(session)) {
    return (
      <PublicBackground>
        <CompletedSession sessionId={session.id} />
      </PublicBackground>
    );
  }

  return (
    <PublicBackground
      withMessagesPane
      participation={participation}
      isLoading={isLoading}
    >
      <ParticipationEditSummary
        participation={participation}
        title={t('booking.bookAccepted.title')}
      />

      <section className={classes.section}>
        {session && (
          <StudyFormatAndRequirements
            session={session}
            title={t('booking.summary.studyFormatAndRequirements')}
            participant={participation.participant}
            dynamicDevices
            singleDeviceWarning={haveDeviceQuota}
          />
        )}

        {displayDeviceSelect && (
          <MeetingDeviceSelection
            session={session}
            onDeviceChange={setSelectedDevice}
          />
        )}
        <CheckboxBlockField
          label={t('booking.summary.aggreement')}
          checked={hasAcceptedRequirements}
          noMargin
          onChange={onToogleAgreementConsent}
          variant="green"
        />
        {!displayAppointmentSelection && (
          <CenteredButton
            onClick={onBookATimeClick}
            text={t('booking.appointment.bookATime')}
            disabled={!hasAcceptedRequirements}
          />
        )}
      </section>

      <div
        ref={hideableSectionRef}
        className={classNames(classes.hideableSection, {
          [classes.hideableSectionVisible]: displayAppointmentSelection,
        })}
      >
        <MeetingDateSelectionForm
          participation={participation}
          submitText={t('booking.bookAccepted.submit')}
          onSubmit={onSelectAppointment}
          paginatedAvailableDates={paginatedAvailableDates}
        />
      </div>

      <VerifyPhoneViaCodeModal
        isOpen={phoneVerificationOpened}
        onRequestClose={closePhoneVerification}
        phone={participant?.phoneNumber || undefined}
        text={
          <Trans
            i18nKey="booking.appointment.phoneVerification"
            values={{
              phone: participant?.phoneNumber
                ? parsePhoneNumber(
                    participant.phoneNumber,
                  )?.formatInternational()
                : '',
            }}
            components={{ br: <br />, b: <strong /> }}
          />
        }
        participantId={participant?.id}
        onSuccess={onPhoneVerified}
      />
    </PublicBackground>
  );
};
